import { Component, inject, OnInit, signal } from '@angular/core';
import { ChooseProductComponent } from '../choose-product/choose-product.component';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { InputModule, SelectComponent, TextareaComponent } from '@beautyapp/form';
import { NzButtonComponent } from 'ng-zorro-antd/button';
import { NzIconDirective } from 'ng-zorro-antd/icon';
import { TranslateModule } from '@ngx-translate/core';
import { NZ_MODAL_DATA, NzModalModule, NzModalRef } from 'ng-zorro-antd/modal';
import { NzColDirective, NzRowDirective } from 'ng-zorro-antd/grid';
import { NzSpaceModule } from 'ng-zorro-antd/space';
import { NzTooltipDirective } from 'ng-zorro-antd/tooltip';
import { VisitService } from '@beautyapp/core';
import { AsyncPipe, DatePipe } from '@angular/common';
import { debounceTime } from 'rxjs/operators';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { NgxPermissionsModule } from 'ngx-permissions';
import { VisitAddFacade } from '@beautyapp/shared';
import { salaryFieldLock } from './salary-exclusivity';

@UntilDestroy()
@Component({
  selector: 'beautyapp-add-realization',
  imports: [
    ChooseProductComponent,
    FormsModule,
    InputModule,
    SelectComponent,
    TextareaComponent,
    NzButtonComponent,
    NzIconDirective,
    ReactiveFormsModule,
    TranslateModule,
    NzModalModule,
    NzColDirective,
    NzRowDirective,
    NzSpaceModule,
    NzTooltipDirective,
    DatePipe,
    AsyncPipe,
    NgxPermissionsModule
],
  templateUrl: './add-realization.component.html',
  styleUrl: './add-realization.component.scss'
})
export class AddRealizationComponent implements OnInit {
  modalRef = inject(NzModalRef);
  visitService = inject(VisitService);
  visitAddFacade = inject(VisitAddFacade);

  data = inject(NZ_MODAL_DATA);

  needChooseProduct = signal(!this.data.realization);
  product = signal<any>(null);

  form = new FormGroup({
    visitId: new FormControl(this.data.visitId, [Validators.required]),
    productId: new FormControl(null, [Validators.required]),
    quantity: new FormControl<number | null>(null),
    gram: new FormControl<number | null>(null),
    price: new FormControl<number | null>(null, [Validators.required]),
    discount: new FormControl<number | null>(null),
    discountPrice: new FormControl<number | null>(null),
    employeeId: new FormControl(null),
    salaryFixed: new FormControl(null),
    // beaut-424: a share of a price cannot exceed the price. The backend
    // rejects >100 with a 422; without this the operator only discovers that
    // at final save, as a status code instead of a message under the field.
    salaryPercent: new FormControl(null, [
      Validators.min(0),
      Validators.max(100),
    ]),
    description: new FormControl(null)
  });

  ngOnInit() {
    console.log(this.data);
    if (this.data.realization) {
      this.form.patchValue({
        productId: this.data.realization.productId,
        quantity: this.data.realization.quantity,
        gram: this.data.realization.gram,
        price: this.data.realization.price,
        discount: this.data.realization.discount,
        discountPrice: this.data.realization.discountPrice,
        employeeId: this.data.realization.employeeId,
        salaryFixed: this.data.realization.salaryFixed,
        salaryPercent: this.data.realization.salaryPercent,
        description: this.data.realization.description
      });
      this.product.set(this.data.realization.product);
    }

    // beaut-627: fixed amount and percent are alternatives, not a sum. With
    // both filled the backend paid the fixed amount and dropped the percent
    // (50% + 500 GEL credited 500). Filling one now clears and locks the other;
    // a saved row that already has both opens with the percent kept, which is
    // the rule the backend applies. Both stay locked until an employee is chosen.
    this.syncSalaryFields();
    this.form.get('salaryFixed')?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.syncSalaryFields());
    this.form.get('salaryPercent')?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.syncSalaryFields());
    this.form.get('employeeId')?.valueChanges
      .pipe(untilDestroyed(this))
      .subscribe(() => this.syncSalaryFields());

    this.form.get('gram')?.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value) {
          this.form.get('quantity')?.setValue(null);
          const gramPrice = this.product().grPerItem ? this.product().salePricePerItem / this.product().grPerItem : 0;
          const price = value * gramPrice;
          this.form.get('price')?.setValue(price);
        }
      });

    this.form.get('quantity')?.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value) {
          this.form.get('gram')?.setValue(null);
          const quantityPrice = this.product().salePricePerItem ? this.product().salePricePerItem * value : 0;
          this.form.get('price')?.setValue(quantityPrice);
        }
      });

    this.form.get('discount')?.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value) {
          const price = this.form.get('price')?.value;
          if (!price) {
            return;
          }
          const discountPrice = price - (price * value / 100);
          this.form.get('discountPrice')?.setValue(discountPrice, {
            emitEvent: false
          });
        }
      });

    this.form.get('discountPrice')?.valueChanges
      .pipe(
        debounceTime(300),
        untilDestroyed(this)
      )
      .subscribe((value) => {
        if (value) {
          const price = this.form.get('price')?.value;
          if (!price) {
            return;
          }
          const discount = (price - value) / price * 100;
          this.form.get('discount')?.setValue(discount, {
            emitEvent: false
          });
        }
      });
  }

  private syncSalaryFields() {
    const fixed = this.form.controls.salaryFixed;
    const percent = this.form.controls.salaryPercent;
    const { lockFixed, lockPercent } = salaryFieldLock(
      fixed.value,
      percent.value,
      !!this.form.controls.employeeId.value
    );

    if (lockFixed && fixed.value != null) {
      fixed.setValue(null, { emitEvent: false });
    }
    if (lockPercent && percent.value != null) {
      percent.setValue(null, { emitEvent: false });
    }
    this.setEnabled(fixed, !lockFixed);
    this.setEnabled(percent, !lockPercent);
  }

  private setEnabled(control: FormControl, enabled: boolean) {
    if (enabled && control.disabled) {
      control.enable({ emitEvent: false });
    } else if (!enabled && control.enabled) {
      control.disable({ emitEvent: false });
    }
  }

  chooseProduct(product: any) {
    this.needChooseProduct.set(false);
    this.product.set(product);
    this.form.patchValue({
      productId: product.id
    });
  }

  submit() {
    this.form.markAllAsTouched();

    if (this.form.valid) {

      const {
        visitId,
        productId,
        quantity,
        gram,
        price,
        discount,
        discountPrice,
        employeeId,
        salaryFixed,
        salaryPercent,
        description
      } = this.form.getRawValue() as any; // beaut-627: the locked salary field is disabled

      if (this.data?.realization?.id) {
        this.visitService
          .updateVisitRealization({
            id: this.data.realization.id,
            visitId,
            productId,
            quantity: +quantity,
            gram: +gram,
            price: +price,
            discount: +discount,
            discountPrice: +discountPrice,
            employeeId,
            salaryFixed: +salaryFixed,
            salaryPercent: +salaryPercent,
            description
          })
          .subscribe(() => {
            this.modalRef.close();
          });
      } else {
        this.visitService.createVisitRealization({
          visitId,
          productId,
          quantity: +quantity,
          gram: +gram,
          price: +price,
          discount: +discount,
          discountPrice: +discountPrice,
          employeeId,
          salaryFixed: +salaryFixed,
          salaryPercent: +salaryPercent,
          description
        })
          .subscribe((res: any) => {
            console.log(res);
            this.modalRef.close();
          });
      }
    }
  }

  onClose(employee: any) {

  }

  addEmployee() {

  }
}
